(function () {
  if (window.customElements.get('trade-in-form')) {
    return;
  }
  const NON_CARDO_SERIAL = '0000000';
  // Visible in cart/checkout only when the shopper was required to enter it.
  const SERIAL_PROP_VISIBLE = 'properties[Trade-In Serial Number]';
  const SERIAL_PROP_HIDDEN = 'properties[_Trade-In Serial Number]';
  // Vendor cart events can be skipped on a thrown fetch; release the lock ourselves.
  const SUBMIT_LOCK_TIMEOUT_MS = 15000;

  function defaultTo(value, fallback) {
    return value == null || value !== value ? fallback : value;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(number) || number == null) {
      return '0';
    }
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const major = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const fraction = parts[1] ? decimal + parts[1] : '';
    return major + fraction;
  }

  // Mirrors theme.js formatMoney so condition/estimate amounts use shop.money_format
  // (JPY amount_no_decimals, currency-code toggle, etc.). Amounts are major units.
  function formatMoneyCents(cents, format) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '';
    const match = formatString.match(placeholderRegex);
    if (!match) {
      return (cents / 100).toFixed(2);
    }

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function stripHtml(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html);
    return (template.content.textContent || '').trim();
  }

  function isOtherOption(name) {
    return String(name).localeCompare('Other', undefined, { sensitivity: 'accent' }) === 0;
  }

  // A–Z, with optional pin (Cardo) first and "Other" last.
  function sortTradeInOptions(names, pinFirst) {
    return names.slice().sort((a, b) => {
      const otherA = isOtherOption(a);
      const otherB = isOtherOption(b);
      if (otherA !== otherB) return otherA ? 1 : -1;
      if (pinFirst) {
        const pinA = a.localeCompare(pinFirst, undefined, { sensitivity: 'accent' }) === 0;
        const pinB = b.localeCompare(pinFirst, undefined, { sensitivity: 'accent' }) === 0;
        if (pinA !== pinB) return pinA ? -1 : 1;
      }
      return a.localeCompare(b);
    });
  }

  function parseTradeInOffersScript(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector('[data-trade-in-offers]');
    if (!el) return null;
    return JSON.parse(el.textContent);
  }

  class TradeInForm extends HTMLElement {
    connectedCallback() {
      const dataElement =
        document.querySelector('[data-trade-in-offers]') ||
        this.querySelector('[data-trade-in-data]');
      if (!dataElement) {
        return;
      }

      try {
        this._data = JSON.parse(dataElement.textContent);
      } catch (error) {
        console.error('[trade-in-form] Failed to parse offers JSON', error);
        return;
      }

      this._toggle = this.querySelector('[data-trade-in-toggle]');
      this._reveal = this.querySelector('[data-trade-in-revealed]');
      this._manufacturerSelect = this.querySelector('[data-trade-in-manufacturer]');
      this._modelSelect = this.querySelector('[data-trade-in-model]');
      this._conditionSelect = this.querySelector('[data-trade-in-condition]');
      this._serialInput = this.querySelector('[data-trade-in-serial]');
      this._serialField = this.querySelector('[data-trade-in-serial-field]');
      this._refundInput = this.querySelector('[data-trade-in-refund]');
      this._refundMaxInput = this.querySelector('[data-trade-in-refund-max]');
      this._skuInput = this.querySelector('[data-trade-in-sku]');
      this._modelHandleInput = this.querySelector('[data-trade-in-model-handle]');
      this._conditionKeyInput = this.querySelector('[data-trade-in-condition-key]');
      this._marketInput = this.querySelector('[data-trade-in-market]');
      this._refundEstimate = this.querySelector('[data-trade-in-refund-estimate]');
      this._agreementCheckbox = this.querySelector('[data-trade-in-agreement]');
      this._agreementLabel = this.querySelector('.trade-in-form__agreement-label');
      this._submitButton = this.querySelector('[data-trade-in-submit]');
      this._cancelButton = this.querySelector('[data-trade-in-cancel]');
      this._currentSerialRequired = false;
      const formId = this.getAttribute('form-id');
      this._productForm = formId ? document.getElementById(formId) : null;
      // Seed from Liquid so the first paint isn't a blind `false` while /cart.js loads.
      this._cartHasTradeIn = this.getAttribute('data-cart-has-trade-in') === 'true';
      this._cartStateVersion = 0;
      this._cartRefreshPromise = null;
      this._cartRefreshAbortController = null;
      this._submitInFlight = false;
      this._submitLockTimeoutId = null;
      this._pendingTradeInSubmit = false;
      this._tradeInSubmitUnlocked = false;

      // Wait for extra paginate pages before first paint so the brand list
      // is never the first-50 subset (Sena and others live past item 50).
      void this._hydrateAllOffers();
      this._ensureClosedState();
      requestAnimationFrame(() => this._ensureClosedState());

      this._boundOnToggleChange = this._onToggleChange.bind(this);
      this._boundOnManufacturerChange = this._onManufacturerChange.bind(this);
      this._boundOnModelChange = this._onModelChange.bind(this);
      this._boundOnConditionChange = this._onConditionChange.bind(this);
      this._boundOnUpdateSubmitEnabled = this._updateSubmitEnabled.bind(this);
      this._boundOnCancelClick = this._onCancelClick.bind(this);
      this._flagSubmit = () => {
        this._pendingTradeInSubmit = true;
      };

      this._toggle.addEventListener('change', this._boundOnToggleChange);
      this._manufacturerSelect.addEventListener('change', this._boundOnManufacturerChange);
      this._modelSelect.addEventListener('change', this._boundOnModelChange);
      this._conditionSelect.addEventListener('change', this._boundOnConditionChange);
      this._serialInput.addEventListener('input', this._boundOnUpdateSubmitEnabled);
      if (this._agreementCheckbox) {
        this._agreementCheckbox.addEventListener('change', this._boundOnUpdateSubmitEnabled);
      }
      this._boundOnAgreementLabelClick = this._onAgreementLabelClick.bind(this);
      if (this._agreementLabel) {
        this._agreementLabel.addEventListener('click', this._boundOnAgreementLabelClick);
        this._agreementLabel.querySelectorAll('a').forEach((link) => {
          if (!link.getAttribute('target')) {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
          }
        });
      }
      if (this._cancelButton) {
        this._cancelButton.addEventListener('click', this._boundOnCancelClick);
      }
      if (this._submitButton) {
        // Safari < 15.4 doesn't populate `event.submitter`; capture intent here.
        this._submitButton.addEventListener('pointerdown', this._flagSubmit);
        this._submitButton.addEventListener('click', this._flagSubmit);
      }
      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onCartNotificationShow = this._onCartNotificationShow.bind(this);
      this._onProductFormSubmit = this._onProductFormSubmit.bind(this);
      this._onPageShow = this._onPageShow.bind(this);
      document.documentElement.addEventListener('cart:updated', this._onCartUpdated);
      // Failed /cart/add.js never fires cart:updated — clear the submit lock here.
      document.addEventListener('cart-notification:show', this._onCartNotificationShow);
      document.addEventListener('submit', this._onProductFormSubmit, true);
      window.addEventListener('pageshow', this._onPageShow);

      this._updateSubmitEnabled();
    }

    disconnectedCallback() {
      this._clearSubmitLockTimer();
      if (this._cartRefreshAbortController) {
        this._cartRefreshAbortController.abort();
      }

      if (this._toggle && this._boundOnToggleChange) {
        this._toggle.removeEventListener('change', this._boundOnToggleChange);
      }
      if (this._manufacturerSelect && this._boundOnManufacturerChange) {
        this._manufacturerSelect.removeEventListener('change', this._boundOnManufacturerChange);
      }
      if (this._modelSelect && this._boundOnModelChange) {
        this._modelSelect.removeEventListener('change', this._boundOnModelChange);
      }
      if (this._conditionSelect && this._boundOnConditionChange) {
        this._conditionSelect.removeEventListener('change', this._boundOnConditionChange);
      }
      if (this._serialInput && this._boundOnUpdateSubmitEnabled) {
        this._serialInput.removeEventListener('input', this._boundOnUpdateSubmitEnabled);
      }
      if (this._agreementCheckbox && this._boundOnUpdateSubmitEnabled) {
        this._agreementCheckbox.removeEventListener('change', this._boundOnUpdateSubmitEnabled);
      }
      if (this._agreementLabel && this._boundOnAgreementLabelClick) {
        this._agreementLabel.removeEventListener('click', this._boundOnAgreementLabelClick);
      }
      if (this._cancelButton && this._boundOnCancelClick) {
        this._cancelButton.removeEventListener('click', this._boundOnCancelClick);
      }
      if (this._submitButton && this._flagSubmit) {
        this._submitButton.removeEventListener('pointerdown', this._flagSubmit);
        this._submitButton.removeEventListener('click', this._flagSubmit);
      }

      document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('cart-notification:show', this._onCartNotificationShow);
      document.removeEventListener('submit', this._onProductFormSubmit, true);
      window.removeEventListener('pageshow', this._onPageShow);
    }

    _ensureClosedState() {
      if (!this._toggle || !this._reveal) {
        return;
      }

      this._toggle.checked = false;
      this._toggle.setAttribute('aria-expanded', 'false');
      this._reveal.hidden = true;
      this._setFieldsEnabled(false);
      this._resetForm();
      this._syncPurchaseControls(false);
      this._updateSubmitEnabled();
    }

    _onPageShow() {
      this._releaseSubmitLock();
      this._ensureClosedState();
    }

    _clearSubmitLockTimer() {
      if (this._submitLockTimeoutId) {
        window.clearTimeout(this._submitLockTimeoutId);
        this._submitLockTimeoutId = null;
      }
    }

    _startSubmitLockTimer() {
      this._clearSubmitLockTimer();
      this._submitLockTimeoutId = window.setTimeout(() => {
        this._submitLockTimeoutId = null;
        if (!this._submitInFlight) return;
        this._releaseSubmitLock();
        this._updateSubmitEnabled();
      }, SUBMIT_LOCK_TIMEOUT_MS);
    }

    _releaseSubmitLock() {
      this._clearSubmitLockTimer();
      this._submitInFlight = false;
      this._tradeInSubmitUnlocked = false;
    }

    _getStickyHeaderOffset() {
      const documentStyles = getComputedStyle(document.documentElement);
      return (
        (Number.parseInt(documentStyles.getPropertyValue('--header-height'), 10) || 0) *
          (Number.parseInt(documentStyles.getPropertyValue('--enable-sticky-header'), 10) || 0) +
        (Number.parseInt(documentStyles.getPropertyValue('--announcement-bar-height'), 10) || 0) *
          (Number.parseInt(documentStyles.getPropertyValue('--enable-sticky-announcement-bar'), 10) || 0)
      );
    }

    _getProductStickyForm() {
      const formId = this.getAttribute('form-id');
      if (!formId) return document.querySelector('product-sticky-form');
      return document.querySelector(`product-sticky-form[form-id="${formId}"]`);
    }

    _getTradeInScrollTopOffset() {
      const documentStyles = getComputedStyle(document.documentElement);
      const anchorOffset = Number.parseInt(documentStyles.getPropertyValue('--anchor-offset'), 10) || 20;
      return this._getStickyHeaderOffset() + anchorOffset;
    }

    _scrollToProductTop() {
      const scope = this.closest('.product__info') || this.closest('section') || document;
      const target = scope.querySelector('.product-meta__title') || this;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _scrollToTradeInForm() {
      const target =
        (this._toggle && this._toggle.closest('.trade-in-form__panel--toggle')) ||
        this;

      const topOffset = this._getTradeInScrollTopOffset();
      const targetRect = target.getBoundingClientRect();
      const scrollTop = window.scrollY + targetRect.top - topOffset;

      window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
    }

    /**
     * The data block is a flat list because Liquid can't deduplicate keys
     * across iterations. We group client-side into:
     *   { Manufacturer: { Model: { conditions, serialRequired, sku } } }
     */
    _groupOffersByManufacturer(offers) {
      const offersByManufacturer = {};
      for (const offer of offers) {
        if (!offer || !offer.manufacturer || !offer.model) continue;
        if (!offersByManufacturer[offer.manufacturer]) {
          offersByManufacturer[offer.manufacturer] = {};
        }
        offersByManufacturer[offer.manufacturer][offer.model] = {
          conditions: offer.conditions || [],
          serialRequired: !!offer.serial_required,
          sku: offer.sku || '',
          handle: offer.handle || '',
        };
      }
      return offersByManufacturer;
    }

    _populateManufacturerOptions() {
      const selected = this._manufacturerSelect.value;
      const placeholder = this._manufacturerSelect.querySelector('option[value=""]');
      this._manufacturerSelect.replaceChildren();
      if (placeholder) this._manufacturerSelect.appendChild(placeholder);
      const manufacturers = sortTradeInOptions(
        Object.keys(this._offersByManufacturer),
        'Cardo'
      );
      for (const manufacturerName of manufacturers) {
        if (!manufacturerName) continue;
        const option = document.createElement('option');
        option.value = manufacturerName;
        option.textContent = manufacturerName;
        this._manufacturerSelect.appendChild(option);
      }
      if (selected) this._manufacturerSelect.value = selected;
    }

    async _hydrateAllOffers() {
      await this._loadRemainingOfferPages();
      this._offersByManufacturer = this._groupOffersByManufacturer(this._data.offers || []);
      this._populateManufacturerOptions();
    }

    async _loadRemainingOfferPages() {
      const pages = Number(this._data && this._data.pages) || 1;
      const pageParam = this._data && this._data.page_param;
      if (pages <= 1 || !pageParam) return;

      try {
        const extra = await Promise.all(
          Array.from({ length: pages - 1 }, (_, i) => {
            const url = new URL(location.href);
            url.searchParams.set(pageParam, String(i + 2));
            return fetch(url.toString(), { credentials: 'same-origin' }).then(async (response) => {
              if (!response.ok) return [];
              const data = parseTradeInOffersScript(await response.text());
              return (data && data.offers) || [];
            });
          })
        );
        const offers = (this._data.offers || []).concat(...extra);
        this._data = { ...this._data, offers };
      } catch (error) {
        console.error('[trade-in-form] Failed to load remaining trade-in offers', error);
      }
    }

    _onToggleChange() {
      const isToggleChecked = this._toggle.checked;
      this._reveal.hidden = !isToggleChecked;
      this._toggle.setAttribute('aria-expanded', isToggleChecked ? 'true' : 'false');

      this._setFieldsEnabled(isToggleChecked);
      this._syncPurchaseControls(isToggleChecked);

      if (isToggleChecked) {
        // Defer the cart lookup until the shopper actually opens the form.
        // Liquid seeds the initial flag; this refresh catches newer cart state.
        this._refreshCartTradeInState();
        this._manufacturerSelect.required = true;
        this._modelSelect.required = true;
        if (!this._manufacturerSelect.value) {
          this._modelSelect.disabled = true;
        }
        if (!this._modelSelect.value) {
          this._conditionSelect.disabled = true;
        }
      } else {
        this._resetForm();
      }

      this._updateSubmitEnabled();

      requestAnimationFrame(() => {
        if (isToggleChecked) {
          this._scrollToTradeInForm();
        } else {
          this._scrollToProductTop();
        }
      });
    }

    _onCancelClick(event) {
      event.preventDefault();
      this._toggle.checked = false;
      this._onToggleChange();
    }

    _setFieldsEnabled(enabled) {
      const fields = [
        this._manufacturerSelect,
        this._modelSelect,
        this._conditionSelect,
        this._serialInput,
        this._refundInput,
        this._refundMaxInput,
        this._skuInput,
        this._modelHandleInput,
        this._conditionKeyInput,
        this._marketInput,
        this._agreementCheckbox,
      ];
      for (const field of fields) {
        if (field) field.disabled = !enabled;
      }
    }

    _syncPurchaseControls(tradeInOpen) {
      const formId = this.getAttribute('form-id');
      if (!formId) {
        return;
      }
      document
        .querySelectorAll(`product-payment-container.product-form__payment-container[form-id="${formId}"]`)
        .forEach((container) => {
          // `inert`, not `disabled`: theme rewrites ATC `disabled` on variant change, and inert also blocks keyboard.
          container.inert = tradeInOpen;
          container.classList.toggle('trade-in-cta-locked', tradeInOpen);
        });

      const stickyForm = this._getProductStickyForm();
      if (stickyForm) {
        stickyForm.classList.toggle('trade-in-sticky-hidden', tradeInOpen);
        if (!tradeInOpen) {
          // Re-run the theme setter: it measured --cart-notification-offset while the form was display:none.
          stickyForm.hidden = stickyForm.hasAttribute('hidden');
        }
      }
    }

    _resetForm() {
      this._manufacturerSelect.value = '';
      this._clearOptions(this._modelSelect);
      this._clearOptions(this._conditionSelect);
      this._setModelPlaceholder(false);
      this._serialInput.value = '';
      if (this._serialField) this._serialField.hidden = true;
      this._refundInput.value = '';
      if (this._refundMaxInput) this._refundMaxInput.value = '';
      if (this._skuInput) this._skuInput.value = '';
      if (this._modelHandleInput) this._modelHandleInput.value = '';
      if (this._conditionKeyInput) this._conditionKeyInput.value = '';
      this._refundEstimate.hidden = true;
      this._refundEstimate.textContent = '';
      if (this._agreementCheckbox) this._agreementCheckbox.checked = false;
      this._applySerialRequirement(null);

      [this._manufacturerSelect, this._modelSelect, this._conditionSelect]
        .forEach((field) => { field.required = false; });
    }

    _onAgreementLabelClick(event) {
      if (event.target.closest('a')) {
        event.stopPropagation();
      }
    }

    _updateSubmitEnabled() {
      if (!this._submitButton) return;
      if (this._submitInFlight) {
        this._submitButton.disabled = true;
        return;
      }
      const isAgreementSatisfied =
        !this._agreementCheckbox || this._agreementCheckbox.checked;
      const isSerialSatisfied =
        !this._currentSerialRequired || this._serialInput.value.trim().length > 0;
      const isSubmitEnabled =
        this._toggle.checked && isAgreementSatisfied && isSerialSatisfied;
      this._submitButton.disabled = !isSubmitEnabled;
    }

    /**
     * Two responsibilities, both gated on the trade-in toggle being on:
     *
     * 1. Submitter is our trade-in submit button: await a fresh cart read,
     *    block if a trade-in is already present, otherwise re-submit once
     *    the one-trade-in gate passes (only one trade-in per order).
     *
     * 2. Submitter is anything else (e.g. Enter in a field, which implicitly
     *    submits the form): block it. Regular purchase is locked while the
     *    toggle is on.
     */
    _onProductFormSubmit(event) {
      if (!this._productForm || event.target !== this._productForm) {
        return;
      }
      if (!this._toggle.checked) {
        return;
      }

      // `event.submitter` is undefined on Safari < 15.4, where it would
      // misclassify a trade-in submit as a regular add-to-cart and strip the
      // trade-in metadata. Fall back to the click/pointerdown intent flag.
      const isTradeInSubmit =
        event.submitter === this._submitButton || this._pendingTradeInSubmit;
      this._pendingTradeInSubmit = false;

      if (isTradeInSubmit) {
        // Second pass after `_gateTradeInSubmit` confirmed the cart is clear.
        if (this._tradeInSubmitUnlocked) {
          this._tradeInSubmitUnlocked = false;
          this._submitInFlight = true;
          this._startSubmitLockTimer();
          if (this._submitButton) {
            this._submitButton.disabled = true;
          }

          // A trade-in is one-per-order, so the page quantity selector must not apply here
          this._forceQuantityToOne();
          this._stampNonCardoSerialForSubmit();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this._gateTradeInSubmit();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * Await a fresh cart read before allowing a trade-in add. Re-submits the
     * product form once the one-trade-in gate passes.
     */
    async _gateTradeInSubmit() {
      if (this._submitInFlight) {
        return;
      }

      this._submitInFlight = true;
      this._startSubmitLockTimer();
      if (this._submitButton) {
        this._submitButton.disabled = true;
      }

      try {
        const hasTradeIn = await this._refreshCartTradeInState({ force: true });
        if (hasTradeIn) {
          this._releaseSubmitLock();
          this._updateSubmitEnabled();
          const message = this.getAttribute('data-already-in-cart-error') || '';
          this._productForm.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: { status: 'error', error: message },
          }));
          return;
        }

        this._tradeInSubmitUnlocked = true;
        this._pendingTradeInSubmit = true;
        if (typeof this._productForm.requestSubmit === 'function' && this._submitButton) {
          this._productForm.requestSubmit(this._submitButton);
        } else {
          // `form.submit()` skips submit handlers, so unlock + force qty here.
          this._tradeInSubmitUnlocked = false;
          this._forceQuantityToOne();
          this._stampNonCardoSerialForSubmit();
          this._startSubmitLockTimer();
          HTMLFormElement.prototype.submit.call(this._productForm);
        }
      } catch (error) {
        this._releaseSubmitLock();
        this._updateSubmitEnabled();
        console.error('[trade-in-form] Failed to gate trade-in submit', error);
      }
    }

    _forceQuantityToOne() {
      if (!this._productForm) return;

      const quantityInputs = Array.from(this._productForm.elements)
        .filter((element) => element && element.name === 'quantity');
      if (!quantityInputs.length) return;

      const previousValues = quantityInputs.map((input) => input.value);
      for (const input of quantityInputs) input.value = '1';

      setTimeout(() => {
        quantityInputs.forEach((input, index) => {
          input.value = previousValues[index];
        });
      }, 0);
    }

    _clearOptions(select) {
      while (select.options.length > 1) select.remove(1);
      select.value = '';
    }

    _setModelPlaceholder(unlocked) {
      if (!this._modelSelect.options.length) return;
      const placeholderOption = this._modelSelect.options[0];
      const placeholderText = unlocked
        ? this._modelSelect.dataset.unlockedPlaceholder
        : this._modelSelect.dataset.lockedPlaceholder;
      if (placeholderText) placeholderOption.textContent = placeholderText;
    }

    _onManufacturerChange() {
      const manufacturer = this._manufacturerSelect.value;

      this._clearOptions(this._modelSelect);
      this._clearOptions(this._conditionSelect);
      this._clearRefundText();
      this._setSku('');
      this._applySerialRequirement(null);
      this._applySerialMode();

      if (!manufacturer || !this._offersByManufacturer[manufacturer]) {
        this._setModelPlaceholder(false);
        this._modelSelect.disabled = true;
        this._conditionSelect.disabled = true;
        return;
      }

      this._setModelPlaceholder(true);

      const models = sortTradeInOptions(
        Object.keys(this._offersByManufacturer[manufacturer])
      );
      for (const modelName of models) {
        const offerEntry = this._offersByManufacturer[manufacturer][modelName];
        const top = this._topCondition(offerEntry);
        if (!top) continue;
        const option = document.createElement('option');
        option.value = modelName;
        option.textContent = this._formatConditionOptionLabel(
          modelName,
          top.amount
        );
        this._modelSelect.appendChild(option);
      }
      this._modelSelect.disabled = false;
      this._conditionSelect.disabled = true;
    }

    _onModelChange() {
      const manufacturer = this._manufacturerSelect.value;
      const model = this._modelSelect.value;

      this._clearOptions(this._conditionSelect);
      this._clearRefundText();

      const offerEntry = (this._offersByManufacturer[manufacturer] || {})[model];

      if (!model || !offerEntry) {
        this._conditionSelect.disabled = true;
        this._setSku('');
        this._setModelHandle('');
        this._applySerialRequirement(null);
        this._applySerialMode();
        return;
      }

      this._setSku(offerEntry.sku || '');
      this._setModelHandle(offerEntry.handle || '');
      this._applySerialRequirement(offerEntry.serialRequired);
      this._applySerialMode();
      this._applyTopCondition(offerEntry);
    }

    _topCondition(offerEntry) {
      const conditions = (offerEntry && offerEntry.conditions) || [];
      const top = conditions.find((condition) => condition.name === 'Condition 1');
      if (!top || top.amount == null || top.amount === '') return null;
      return top;
    }

    _applyTopCondition(offerEntry) {
      this._clearOptions(this._conditionSelect);
      const condition = this._topCondition(offerEntry);
      if (!condition) {
        this._conditionSelect.disabled = true;
        this._clearRefundText();
        return;
      }

      const label = condition.display_name || condition.name;
      const option = document.createElement('option');
      option.value = label;
      option.textContent = this._formatConditionOptionLabel(label, condition.amount);
      option.dataset.conditionKey = condition.name || '';
      option.dataset.amount = condition.amount;
      option.dataset.amountMax =
        condition.amount_max == null ? '' : condition.amount_max;
      this._conditionSelect.appendChild(option);
      this._conditionSelect.disabled = false;
      this._conditionSelect.value = label;
      this._onConditionChange();
    }

    _formatMoneyAmount(amount) {
      const majorUnits = Number.parseFloat(amount);
      if (!Number.isFinite(majorUnits)) {
        return String(amount ?? '');
      }

      const cents = Math.round(majorUnits * 100);
      const settings = window.themeVariables && window.themeVariables.settings;
      const format = settings
        ? settings.currencyCodeEnabled
          ? settings.moneyWithCurrencyFormat
          : settings.moneyFormat
        : '';

      if (format) {
        return stripHtml(formatMoneyCents(cents, format));
      }

      const locale = (window.Shopify && window.Shopify.locale) || undefined;
      const currency =
        window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (currency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format(majorUnits);
        } catch (error) {
          // Invalid currency code — fall through.
        }
        try {
          return `${new Intl.NumberFormat(locale, {
            style: 'decimal',
            maximumFractionDigits: 2,
          }).format(majorUnits)} ${currency}`;
        } catch (error) {
          return String(majorUnits);
        }
      }

      return String(majorUnits);
    }

    _formatConditionOptionLabel(label, amount) {
      return `${label} - ${this._formatMoneyAmount(amount)}`;
    }

    _setSku(sku) {
      if (this._skuInput) this._skuInput.value = sku || '';
    }

    _setModelHandle(handle) {
      if (this._modelHandleInput) this._modelHandleInput.value = handle || '';
    }

    _setConditionKey(conditionKey) {
      if (this._conditionKeyInput) this._conditionKeyInput.value = conditionKey || '';
    }

    _applySerialRequirement(serialRequired) {
      const isRequired = serialRequired === true;
      this._currentSerialRequired = isRequired;
      this._serialInput.required = isRequired;
      this._syncSerialPropertyName();
      if (this._serialField) {
        this._serialField.hidden = !isRequired;
      }
      this._updateSubmitEnabled();
    }

    /**
     * Shopify hides line properties whose keys start with `_`. Keep a
     * placeholder/optional serial on the line for ops, but only surface it in
     * the cart when the shopper was required to type one.
     */
    _syncSerialPropertyName() {
      if (!this._serialInput) return;
      this._serialInput.name = this._currentSerialRequired
        ? SERIAL_PROP_VISIBLE
        : SERIAL_PROP_HIDDEN;
    }

    /** Serial field visible only when the selected offer requires one. */
    _offerRequiresSerial() {
      const offer = this._selectedOffer();
      return Boolean(offer && offer.serialRequired);
    }

    _selectedOffer() {
      const manufacturer = this._manufacturerSelect && this._manufacturerSelect.value;
      const model = this._modelSelect && this._modelSelect.value;
      if (!manufacturer || !model) return null;
      return (this._offersByManufacturer[manufacturer] || {})[model] || null;
    }

    _applySerialMode() {
      const requiresSerial = this._offerRequiresSerial();
      if (this._serialField) this._serialField.hidden = !requiresSerial;

      if (requiresSerial) {
        if (this._serialInput.value === NON_CARDO_SERIAL) this._serialInput.value = '';
      } else {
        this._stampNonCardoSerialForSubmit();
      }

      this._syncSerialPropertyName();
      this._updateSubmitEnabled();
    }

    /**
     * Non-Cardo devices have no real serial — always stamp zeroes on the
     * line (hidden `_` property) so cart + Priority Create RT both see it.
     * Disabled inputs are omitted from FormData, so re-enable before submit.
     */
    _stampNonCardoSerialForSubmit() {
      if (!this._serialInput || this._offerRequiresSerial()) return;
      this._serialInput.disabled = false;
      this._serialInput.required = false;
      this._serialInput.value = NON_CARDO_SERIAL;
      this._currentSerialRequired = false;
      this._syncSerialPropertyName();
    }

    _onConditionChange() {
      const selectedOption =
        this._conditionSelect.options[this._conditionSelect.selectedIndex];
      const amount = selectedOption ? selectedOption.dataset.amount : '';
      const amountMax = selectedOption ? selectedOption.dataset.amountMax : '';
      this._setConditionKey(
        selectedOption ? selectedOption.dataset.conditionKey || '' : ''
      );

      if (!amount) {
        this._clearRefundText();
        return;
      }
      const displayAmount = this._formatMoneyAmount(amount);

      this._refundInput.value = amount;
      if (this._refundMaxInput) {
        this._refundMaxInput.value = amountMax || '';
      }

      const template = this._data.refundEstimateTemplate || '';
      this._refundEstimate.textContent = template.replace('__AMOUNT__', displayAmount);
      this._refundEstimate.hidden = false;
    }

    _clearRefundText() {
      this._refundInput.value = '';
      if (this._refundMaxInput) this._refundMaxInput.value = '';
      this._setConditionKey('');
      this._refundEstimate.hidden = true;
      this._refundEstimate.textContent = '';
    }

    async _refreshCartTradeInState({ force = false } = {}) {
      if (!force && this._cartRefreshPromise) {
        return this._cartRefreshPromise;
      }

      if (this._cartRefreshAbortController) {
        this._cartRefreshAbortController.abort();
      }

      const requestVersion = ++this._cartStateVersion;
      this._cartRefreshAbortController = new AbortController();
      const { signal } = this._cartRefreshAbortController;

      this._cartRefreshPromise = (async () => {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal,
          });
          if (!response.ok) {
            return this._cartHasTradeIn;
          }

          const cart = await response.json();
          // Ignore stale responses that finished after a newer refresh/update.
          if (requestVersion !== this._cartStateVersion) {
            return this._cartHasTradeIn;
          }

          this._cartHasTradeIn = this._cartContainsTradeIn(cart);
          return this._cartHasTradeIn;
        } catch (error) {
          if (error?.name === 'AbortError') {
            return this._cartHasTradeIn;
          }
          console.error('[trade-in-form] Failed to refresh cart trade-in state', error);
          return this._cartHasTradeIn;
        } finally {
          if (requestVersion === this._cartStateVersion) {
            this._cartRefreshPromise = null;
          }
        }
      })();

      return this._cartRefreshPromise;
    }

    _onCartUpdated(event) {
      // Invalidate any in-flight /cart.js so a late empty-cart response can't
      // overwrite the fresher cart:updated payload.
      this._cartStateVersion += 1;
      if (this._cartRefreshAbortController) {
        this._cartRefreshAbortController.abort();
      }
      this._cartRefreshPromise = null;
      this._releaseSubmitLock();

      const cart = event?.detail?.cart;
      if (cart && Array.isArray(cart.items)) {
        this._cartHasTradeIn = this._cartContainsTradeIn(cart);
        if (this._cartHasTradeIn) {
          // Trade-in landed in cart — close toggle and clear all fields.
          this._ensureClosedState();
          return;
        }
        this._updateSubmitEnabled();
        return;
      }

      this._refreshCartTradeInState({ force: true }).then((hasTradeIn) => {
        if (hasTradeIn) {
          this._ensureClosedState();
        } else {
          this._updateSubmitEnabled();
        }
      });
    }

    /**
     * Theme dispatches cart-notification:show on both success and failure of
     * /cart/add.js, but cart:updated only on success. Without this, a failed
     * add leaves _submitInFlight true and trade-in submit silently no-ops.
     * Network-level throws never reach this event — `_startSubmitLockTimer`
     * is the fallback for that path.
     */
    _onCartNotificationShow(event) {
      if (!this._submitInFlight) return;
      if (event?.detail?.status !== 'error') return;
      this._releaseSubmitLock();
      this._updateSubmitEnabled();
    }

    _cartContainsTradeIn(cart) {
      if (window.CardoTradeIn && typeof window.CardoTradeIn.cartHasTradeIn === 'function') {
        return window.CardoTradeIn.cartHasTradeIn(cart);
      }
      const items = (cart && cart.items) || [];
      return items.some((item) => {
        if (!item || !item.properties) return false;
        if (Array.isArray(item.properties)) {
          return item.properties.some((propertyPair) =>
            this._isTradeInPropertyKey(propertyPair && propertyPair[0])
          );
        }
        return Object.keys(item.properties).some((propertyKey) =>
          this._isTradeInPropertyKey(propertyKey)
        );
      });
    }

    _isTradeInPropertyKey(key) {
      if (window.CardoTradeIn && typeof window.CardoTradeIn.isTradeInPropertyKey === 'function') {
        return window.CardoTradeIn.isTradeInPropertyKey(key);
      }
      return typeof key === 'string' && key.includes('Trade-In');
    }
  }

  window.customElements.define('trade-in-form', TradeInForm);
})();
